import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { API } from "../lib/api";
import PhotoCard from "../components/PhotoCard";
import EditorialMarquee from "../components/Marquee";

const HERO_IMG =
  "https://images.unsplash.com/photo-1529651121800-01d45d421ec9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHw0fHxkcmFtYXRpYyUyMGxhbmRzY2FwZSUyMHBob3RvZ3JhcGh5fGVufDB8fHx8MTc5MjI3NDI3M3ww&ixlib=rb-4.1.0&q=85";

function MaskedLine({ children, delay }) {
  return (
    <span className="mask-line">
      <motion.span
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ delay, duration: 1, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [total, setTotal] = useState(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    API.get("/photos", { params: { featured: true } })
      .then((r) => setFeatured(r.data.slice(0, 6)))
      .catch(() => {});
    API.get("/photos")
      .then((r) => setTotal(r.data.length))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden" data-testid="hero-section">
        <motion.div style={{ y: bgY }} className="absolute inset-0 -bottom-32">
          <motion.img
            src={HERO_IMG}
            alt="Dramatic landscape"
            initial={{ scale: 1.15 }}
            animate={{ scale: 1 }}
            transition={{ duration: 6, ease: "easeOut" }}
            className="w-full h-full object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-black/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-black/30" />

        <motion.div style={{ opacity: fade }} className="relative z-10 w-full max-w-[1600px] mx-auto px-6 md:px-12 pb-24 md:pb-32">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 1 }}
            className="text-[10px] md:text-xs tracking-[0.2em] md:tracking-[0.3em] uppercase font-medium text-[#D4AF37] mb-8"
            data-testid="hero-overline"
          >
            Fine Art Photography — Digital Editions
          </motion.p>
          <h1 className="font-display text-6xl md:text-[8vw] leading-[0.9] tracking-tighter text-white" data-testid="hero-title">
            <MaskedLine delay={0.35}>Light, held</MaskedLine>
            <MaskedLine delay={0.5}>
              <em className="text-[#D4AF37]">forever.</em>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9 }}
            className="mt-8 max-w-md text-base text-zinc-300 leading-relaxed"
            data-testid="hero-subtitle"
          >
            A curated gallery of limited digital photographs. Own the frame,
            in full resolution, the moment you choose it.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.9 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/gallery"
              data-testid="hero-explore-button"
              className="inline-flex items-center gap-3 bg-white text-black text-xs tracking-[0.2em] uppercase font-semibold px-10 py-5 hover:bg-zinc-200 hover:-translate-y-0.5 transition-all duration-300"
            >
              Explore the Gallery <ArrowRight size={15} />
            </Link>
            <a
              href="#featured"
              data-testid="hero-featured-button"
              className="inline-flex items-center gap-3 border border-white/25 text-white text-xs tracking-[0.2em] uppercase font-semibold px-10 py-5 hover:border-white hover:-translate-y-0.5 transition-all duration-300"
            >
              Selected Works <ArrowDown size={15} />
            </a>
          </motion.div>
        </motion.div>
      </section>

      <EditorialMarquee />

      <section id="featured" className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-36" data-testid="featured-section">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <span className="text-xs tracking-[0.2em] uppercase font-medium text-zinc-500 block mb-4">The Collection</span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tighter text-white">
              Selected <em className="text-[#D4AF37]">works</em>
            </h2>
          </div>
          <Link
            to="/gallery"
            data-testid="view-all-link"
            className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-medium text-zinc-400 hover:text-white transition-colors duration-300"
          >
            View all photographs <ArrowRight size={14} />
          </Link>
        </div>
        <div className="columns-1 sm:columns-2 xl:columns-3 gap-6 md:gap-8">
          {featured.map((photo, i) => (
            <PhotoCard key={photo.id} photo={photo} index={i} natural className="mb-6 md:mb-8 break-inside-avoid" />
          ))}
        </div>
      </section>

      <section id="about" className="border-t border-white/10 bg-[#0A0A0A]" data-testid="about-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-36 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-7">
            <span className="text-xs tracking-[0.2em] uppercase font-medium text-zinc-500 block mb-8">The Studio</span>
            <p className="font-display text-3xl md:text-5xl leading-[1.15] tracking-tight text-white">
              Every photograph here was made slowly — waited for, walked toward,
              and earned. Each is released as a{" "}
              <em className="text-[#D4AF37]">high-resolution digital edition</em>,
              licensed for the walls of your life and work.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col justify-end gap-10">
            {[
              [total === null ? "—" : String(total), "Original works in the collection"],
              ["8K", "Maximum download resolution"],
            ].map(([num, label]) => (
              <div key={label} className="flex items-baseline gap-6 border-b border-white/10 pb-6">
                <span className="font-display text-4xl md:text-5xl text-[#D4AF37]">{num}</span>
                <span className="text-sm text-zinc-400">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
